import Chip from "@material-ui/core/Chip";
import ReactECharts from "echarts-for-react";
import {
  getCountryCode,
  getFlagUrl,
} from "../../../SysGuard/Dashboard/ContentPages/SysGuardColumnConfig";
import remove_public_file from "../../../../assets/vizr_images/remove_public_file.svg";
import alert_warning_amber from "../../../../assets/vizr_images/alert_warning_amber.svg";
import done_all from "../../../../assets/vizr_images/done_all.svg";
import alert_off_notify from "../../../../assets/vizr_images/alert_off_notify.svg";
import NotificationsOffIcon from '@material-ui/icons/NotificationsOff';
import NotificationsIcon from '@material-ui/icons/Notifications';
import { maskUsername } from "../../../SysGuard/SysGuardCommonComp";
import { startCase, camelCase, isNull } from "lodash";
import { formatNumber, PUBLIC_ACCESS_REVOKE_USER, renderCountryNamesWithPopover } from "../../Utility/DataguardConstant";
import { calculatePercentage } from "../../../SysGuard/sysGuardConstants";
import SeverityChips from "../../DataGuardCommonComponents";
import { renderFlag } from "../../Utility/DataguardConstant";

const getEchartColors = () => {
  return [
    "#5470C6",
    "#FAC858",
    "#EE6666",
    "#73C0DE",
    "#FC8452",
    "#9A60B4",
    "#EA7CCC",
  ];
};

export const processCountryData = (value) => {
  if (!value) return [];

  if (Array.isArray(value)) {
    // Handle array of objects with country property
    if (value.length > 0 && typeof value[0] === 'object' && "country" in value[0]) {
      const unique = [...new Set(value.map((item) => item.country))];
      return unique.filter(country => country && country !== "Not Found" && country !== "Unknown" && country !== null);
    }
    // Handle array of strings
    return value.filter(country => country && country !== "Not Found" && country !== "Unknown" && country !== null);
  }

  if (typeof value === 'string') {
    return value.split(",")
      .map((name) => name.trim())
      .filter(country => country && country !== "Not Found" && country !== "Unknown" && country !== null);
  }

  return [];
};

// Reusable legend component for pie/donut charts
const ChartLegend = ({ data, overrideColor }) => {
  const customColors = getEchartColors();

  const legendItems = data.map((item, idx) => {
    const color =
      item.itemStyle?.color || overrideColor?.[item.name] || customColors[idx % customColors.length];
    return {
      name: item.name,
      color,
      originalValue: item.originalValue ? item.originalValue : item.value,
    };
  });
  return (
    <div className="legend-container">
      {legendItems.map((item, idx) => (
        <div key={idx} className="legend-item">
          <span
            className="legend-color"
            style={{ background: item.color }}
          ></span>
          <span className="legend-txt">{item.name}</span>
          <span className="legend-txt">
            ({formatNumber(item.originalValue)})
          </span>
        </div>
      ))}
    </div>
  );
};

const renderEmptyAnomaly = () => {
  const data = [{
    value: 0,
    name: "No Anomalies",
    itemStyle: {
      color: "#4CAF50", // Green color
    },
  }];

  const option = {
    tooltip: {
      trigger: "item",
      formatter: function (params) {
        return params.name;
      },
    },
    legend: {
      show: false,
    },
    series: [
      {
        name: "Anomalies",
        type: "pie",
        radius: ["60%", "90%"],
        minAngle: 10,
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: false,
          },
        },
        labelLine: {
          show: false,
        },
        data: data,
      },
    ],
  };

  return (
    <div className="details-chart-container with-legend">
      <ReactECharts
        option={option}
        style={{ height: "75px", width: "75px" }}
        notMerge={true}
        opts={{ renderer: "canvas" }}
      />
      <ChartLegend data={data} />
    </div>
  );
};

const renderEmptyAssets = () => {
  const data = [{
    value: 0,
    name: "No Assets",
    itemStyle: {
      color: "#8b8e8bff",
    },
  }];

  const option = {
    tooltip: {
      trigger: "item",
      formatter: function (params) {
        return params.name;
      },
    },
    legend: {
      show: false,
    },
    series: [
      {
        name: "Assets",
        type: "pie",
        radius: "90%",
        center: ["50%", "50%"],
        minAngle: 10,
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: false,
          },
        },
        labelLine: {
          show: false,
        },
        data: data,
      },
    ],
  };

  return (
    <div className="details-chart-container with-legend">
      <ReactECharts
        option={option}
        style={{ height: "75px", width: "75px" }}
        notMerge={true}
        opts={{ renderer: "canvas" }}
      />
      <ChartLegend data={data} />
    </div>
  );
};

// currently not used but may be useful later
const renderTableHeatmap = (values) => {
  if (!values || values.length === 0) return renderEmptyAnomaly();
  const data = values.map((v, index) => {
    return {
      value: [
        index,
        0,
        v.count,
        formatNumber(v.count),
        v.dataSourceName + " (" + v.dataSourceType + "): " + v.count,
      ],
      itemStyle: v.count === 0 ? { color: "#4CAF50" } : {},
    };
  });
  const maxAccesses =
    values.length > 0 ? Math.max(...values.map((v) => v.count)) : 0;
  const cellTotalWidth = 45;
  const option = {
    tooltip: {
      trigger: "item",
      formatter: function (params) {
        return params.value[4]; // Shows v.dataSourceName + " (" + v.dataSourceType + ")"
      },
    },
    grid: {
      top: "10%",
      bottom: "10%",
      left: "10%",
      right: "10%",
      containLabel: true,
    },
    xAxis: {
      type: "category",
      splitArea: { show: false },
      splitLine: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      data: values.map((_, i) => i),
    },
    yAxis: {
      type: "category",
      splitArea: { show: false },
      splitLine: { show: false },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { show: false },
      data: [0],
    },
    visualMap: {
      show: false,
      dimension: 2,
      min: 0,
      max: maxAccesses,
      inRange: {
        color: ["#ffcccc", "#ff0000"],
      },
      calculable: true,
      realtime: true,
    },
    series: [
      {
        name: "User Activity",
        type: "heatmap",
        data: data,
        cellSize: [cellTotalWidth, cellTotalWidth],
        label: {
          show: true,
          position: "inside",
          formatter: function (params) {
            return `${params.value[3]}`;
          },
          fontSize: 14,
          color: "#FFFFFF",
        },
        itemStyle: {
          borderWidth: 2,
          borderColor: "#fff",
          borderRadius: 5,
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: "rgba(0, 0, 0, 0.5)",
          },
        },
      },
    ],
  };
  return (
    <div style={{ display: "flex", justifyContent: "center", width: "100%" }}>
      <ReactECharts
        option={option}
        style={{
          height: `${cellTotalWidth + 4}px`,
          width: `${(cellTotalWidth + 4) * values.length}px`,
        }}
        notMerge={true}
        opts={{
          renderer: "canvas",
        }}
      />
    </div>
  );
};

export const renderDonutChart = (values, isFullNName) => {
  if (!values) return "-";

  // Transform the data to ECharts format
  const customColors = getEchartColors();
  let data = values;
  if (values.length > 1) {
    data = data
    .filter((item) => (item?.count || 0) > 0) // Filter out zero values
  }
  data = data
    .map((item, index) => ({
      value: item?.count || 0,
      name: isFullNName ? item.name : item?.dataSourceName + " (" + item?.dataSourceType + ")" || "Unknown",
      itemStyle: {
        color: item.color || customColors[index % customColors.length],
      },
    }));

  // If all values are zero, return empty state
  if (data.length === 0) return "-";

  const option = {
    tooltip: {
      trigger: "item",
      formatter: function (params) {
        return params.name + ": " + params.value;
      },
    },
    legend: {
      show: false,
    },
    color: customColors,
    series: [
      {
        name: "Access From",
        type: "pie",
        radius: ["60%", "90%"],
        minAngle: 10,
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 10,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: false,
            fontSize: 40,
            fontWeight: "bold",
          },
        },
        labelLine: {
          show: false,
        },
        data: data,
      },
    ],
  };

  return (
    <div className="details-chart-container with-legend">
      <ReactECharts
        option={option}
        style={{ height: "75px", width: "75px" }}
        notMerge={true}
        opts={{ renderer: "canvas" }}
      />
      <ChartLegend data={data} />
    </div>
  );
};

const renderPolarChart = (values) => {
  if (!values || values.length === 0) return renderEmptyAssets();
  // Transform the data to ECharts format with log scale
  const customColors = getEchartColors();
  const data = values.map((item, index) => ({
    value: Math.log10(item.count),
    name: item?.dataSourceName + " (" + item?.dataSourceType + ")" || "Unknown",
    originalValue: item?.count || 0,
    itemStyle: {
      color: customColors[index % customColors.length],
    },
  }));
  const option = {
    tooltip: {
      trigger: "item",
      formatter: function (params) {
        return params.name + ": " + params.data.originalValue;
      },
    },
    legend: {
      show: false,
    },
    toolbox: {
      show: false,
    },
    color: customColors,
    series: [
      {
        name: "Pie Chart",
        type: "pie",
        minAngle: 10,
        radius: "90%",
        center: ["50%", "50%"],
        itemStyle: {
          borderRadius: 8,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: false,
          },
        },
        data: data,
      },
    ],
  };

  return (
    <div className="details-chart-container with-legend">
      <ReactECharts
        option={option}
        style={{ height: "75px", width: "75px" }}
        notMerge={true}
        opts={{ renderer: "canvas" }}
      />
      <ChartLegend data={data} />
    </div>
  );
};

const renderRadialChart = (values) => {
  if (!values || values.length === 0) return "-";

  // Extract overallTotalActivities and countryTotalActivities from the values
  const overallTotal = values[0]?.overallTotalActivities || 0;
  const countryTotal = values[0]?.countryTotalActivities || 0;
  const countryName = values[0]?.countryName || "Country";
  if (overallTotal === 0) return "-";

  // Calculate percentage
  const countryPercentage = calculatePercentage(countryTotal, overallTotal);
  const remainingPercentage = 100 - countryPercentage;

  const data = [
    {
      value: countryPercentage,
      originalValue: countryTotal,
      name: countryName + " Activities",
      tooltip: `Activities from ${countryName}: ${countryTotal.toLocaleString()} (${countryPercentage}%)`,
      itemStyle: {
        color: "#1f77b4", // Blue color for current country
      },
    },
    {
      value: remainingPercentage,
      originalValue: overallTotal - countryTotal,
      name: "Other Activities",
      tooltip: `Activities from other countries: ${(
        overallTotal - countryTotal
      ).toLocaleString()} (${remainingPercentage}%)`,
      itemStyle: {
        color: "#d3d3d3", // Grey color for remaining activities
      },
    },
  ];

  const option = {
    tooltip: {
      trigger: "item",
      formatter: function (params) {
        return params.data.tooltip;
      },
    },
    legend: {
      show: false,
    },
    graphic: [
      {
        type: "text",
        left: "center",
        top: "center",
        style: {
          text: formatNumber(countryTotal),
          fontSize: 12,
          fontWeight: "bold",
          fill: "#333",
          textAlign: "center",
          textVerticalAlign: "middle",
        },
      },
    ],
    series: [
      {
        name: "Activities",
        type: "pie",
        minAngle: 10,
        radius: ["60%", "90%"], // Donut chart
        center: ["50%", "50%"],
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 4,
          borderColor: "#fff",
          borderWidth: 2,
        },
        label: {
          show: false,
        },
        emphasis: {
          label: {
            show: false,
          },
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: "rgba(0, 0, 0, 0.5)",
          },
        },
        labelLine: {
          show: false,
        },
        data: data,
      },
    ],
  };

  return (
    <div className="details-chart-container">
      <ReactECharts
        option={option}
        style={{ height: "75px", width: "75px" }}
        notMerge={true}
        opts={{ renderer: "canvas" }}
      />
    </div>
  );
};

const renderHeaderWithFilters =
  (
    headerName,
    field,
    isSelected,
    activeFilterField,
    handleFilterIconClick,
    showFilter = true
  ) =>
    (params) =>
    (
      <div className="dataGuardFilterContainer">
        <span>{headerName}</span>
        {showFilter && (
          <span
            className={`dataGuardTableFilterIcon ${isSelected || activeFilterField === field ? "visible" : ""
              }`}
            onClick={(e) => handleFilterIconClick(e, field)}
          >
            <img
              src={
                isSelected
                  ? "/sysGuardIcons/filter_list.svg"
                  : "/sysGuardIcons/filter_list_notSelected.svg"
              }
              alt="filter-icon"
            />
          </span>
        )}
      </div>
    );

// Function to handle notification icon click and show confirmation popup
const handleNotificationClick = (value, setNotificationDetails, setNotificationPopup) => {
  const notificationDetails = {
    policyId: value?.policyId,
    policyName: value?.policyName,
    currentState: value?.notification
  };
  setNotificationDetails(notificationDetails);
  setNotificationPopup(true);
};

// Function to handle alert action icon click and show confirmation popup
const handleAlertActionClick = (value, setAlertActionDetails, setAlertActionPopup) => {
  const alertActionDetails = {
    policyId: value?.policyId,
    policyName: value?.policyName,
    severity: value?.severity
  };
  setAlertActionDetails(alertActionDetails);
  setAlertActionPopup(true);
};

export const PAGE_CONFIGURATIONS = (
  setRevokeDetails,
  setRevokePopup,
  dispatch,
  isMasked,
  locationFiltersList,
  policySubtypeFiltersList,
  severityFiltersList,
  userRolesFiltersList,
  ownersFiltersList,
  handleFilterIconClick,
  activeFilterField,
  isIncognito,
  setNotificationDetails = () => { },
  setNotificationPopup = () => { },
  setAlertActionDetails = () => { },
  setAlertActionPopup = () => { }
) => ({
  locations: {
    id: "locations",
    title: "Location Details",
    apiEndpoint: "/dataguard/location/data",
    FiltersListEndPoint: "/dataguard/locations/distinct",
    table: {
      pagination: true,
      columns: [
        {
          id: "location",
          title: "Location",
          align: "left",
          width: 200,
          sorting: true,
          renderHeader: renderHeaderWithFilters(
            "Location",
            "location",
            locationFiltersList?.length,
            activeFilterField,
            handleFilterIconClick
          ),
          renderCell: (value) => (
            <>
              <div className="location-name-cell" title={value}>
                {renderFlag(value)} {value}
              </div>
            </>
          ),
        },
        {
          id: "usersAccumulated",
          title: "# of Users",
          width: 200,
          sorting: true,
          renderCell: (value) => {
            if (!value || value.length === 0 || Object.values(value) === 0)
              return "-";
            const totalValue = Object.values(value).reduce(
              (sum, v) => sum + v.count,
              0
            );
            return (
              <>
                <Chip
                  size="medium"
                  title="Total Users"
                  label={totalValue}
                  color="primary"
                  className="user-chip"
                />
              </>
            );
          },
        },
        {
          id: "activitiesAccumulated",
          title: "# of Activities",
          width: 250,
          renderCell: (value) => {
            if (!value || value.length === 0 || Object.values(value) === 0)
              return "-";
            return renderRadialChart(Object.values(value));
          },
        },
        {
          id: "anomalyAccumulated",
          title: "# of Anomalies",
          width: 200,
          align: "left",
          renderCell: (value) => {
            if (!value || Object.keys(value).length === 0) return renderEmptyAnomaly();
            const valueArr = Object.values(value).map((v) => ({
              count: v.count,
              dataSourceName: v.dataSourceName,
              dataSourceType: v.dataSourceType,
            }));
            const totalCount = valueArr.reduce((sum, v) => sum + (v.count || 0), 0);
            if (totalCount === 0) return renderEmptyAnomaly();
            return renderDonutChart(valueArr);
          },
        },
        {
          id: "uniqueDataAssetsAccumulated",
          title: "Unique Data Assets",
          align: "left",
          width: 250,
          renderCell: (value) => {
            if (!value || value.length === 0 || Object.values(value).length === 0)
              return renderEmptyAssets();
            return renderPolarChart(Object.values(value));
          },
        },
      ],
    },
  },
  roles: {
    id: "roles",
    title: "Role Details",
    apiEndpoint: "/dataguard/userRoles/data",
    FiltersListEndPoint: "/dataguard/userRoles/distinct",
    table: {
      pagination: true,
      columns: [
        {
          id: "roleName",
          title: "Role",
          align: "left",
          width: "16%",
          sorting: true,
          renderHeader: renderHeaderWithFilters(
            "Role",
            "roleName",
            userRolesFiltersList?.length,
            activeFilterField,
            handleFilterIconClick
          ),
          renderCell: (value) => {
            return (
              <div className="location-name-cell" title={value}>
                {value}
              </div>
            );
          },
        },
        {
          id: "users",
          title: "# of Users",
          width: "15%",
          sorting: true,
          renderCell: (value) => {
            if (!value || value.length === 0 || Object.values(value) === 0)
              return "-";
            const totalValue = Object.values(value).reduce(
              (sum, v) => sum + v.usersCount,
              0
            );
            return (
              <Chip
                size="medium"
                title="Total Users"
                label={totalValue}
                color="primary"
                className="user-chip"
              />
            );
          },
        },
        {
          id: "activeUsersAccumulated",
          title: "Active Users",
          width: "15%",
          sorting: true,
          renderCell: (value) => {
            if (!value || value.length === 0 || Object.values(value) === 0)
              return "-";
            return (
              <Chip
                size="medium"
                title="Total Active Users"
                label={Object.values(value).reduce(
                  (sum, v) => sum + v.count,
                  0
                )}
                color="primary"
                className="user-chip"
              />
            );
          },
        },
        {
          id: "percentageOfTotalUsers",
          title: "Total Users",
          width: "15%",
          renderCell: (v) =>
            v !== undefined ? (
              <Chip
                size="medium"
                title={`Total % of Users for this Role: ${v}%`}
                label={`${Math.floor(v)}%`} // shows only the integer part
                color="primary"
                className="user-chip"
              />
            ) : (
              "-"
            ),
        },
        {
          id: "anomaliesAccumulated",
          title: "# of Anomalies",
          width: "16%",
          align: "left",
          renderCell: (value) => {
            if (!value || value.length === 0 || Object.values(value) === 0)
              return renderEmptyAnomaly();
            const valueArr = Object.values(value).map((v) => ({
              count: v.count,
              dataSourceName: v.dataSourceName,
              dataSourceType: v.dataSourceType,
            }));
            const totalCount = valueArr.reduce((sum, v) => sum + (v.count || 0), 0);
            if (totalCount === 0) return renderEmptyAnomaly();
            return renderDonutChart(valueArr);
          },
        },
        {
          id: "locations",
          title: "Locations",
          width: "19%",
          align: "left",
          renderCell: (value) => {
            if (!value || value.length === 0 || Object.values(value) === 0)
              return "-";

            const validLocations = processCountryData(value);
            if (validLocations.length === 0) return "-";

            // Use the new generic function with internal state management
            return renderCountryNamesWithPopover(
              validLocations,
              3, // Show 3 countries before +remaining
              "location-flag-cell"
            );
          },
        },
      ],
    },
  },

  publicdata: {
    id: "publicdata",
    title: "Public Data Details",
    apiEndpoint: "/dataguard/publicDataAssets/data",
    FiltersListEndPoint: "/dataguard/publicDataAssets/owners/distinct",
    table: {
      pagination: true,
      sorting: true,
      selection: true,
      columns: [
        {
          id: "ownerName",
          title: "Owner Name",
          align: "left",
          width: "19%",
          sorting: true,
          renderHeader: !isIncognito
            ? renderHeaderWithFilters(
              "Owner Name",
              "ownerName",
              ownersFiltersList?.length,
              activeFilterField,
              handleFilterIconClick
            )
            : () => <span>Owner Name</span>,
          renderCell: (value, isIncognito) => {
            const displayName = isIncognito
              ? maskUsername(value)
              : startCase(camelCase(value));
            return (
              <div className="location-name-cell" title={displayName}>
                {displayName}
              </div>
            );
          },
        },
        {
          id: "assetsAccumulated",
          title: "% of Public Assets",
          width: "19%",
          sorting: true,
          renderCell: (value) => {
            if (!value || Object.keys(value).length === 0) return "-";
            const totalCount = Object.values(value).reduce(
              (sum, v) => sum + (v.count || 0),
              0
            );
            const totalAssets = Object.values(value).reduce(
              (sum, v) => sum + (v.totalAssetsCount || 0),
              0
            );
            if (totalAssets === 0) return "-";
            const percentage = (totalCount / totalAssets) * 100;
            const roundedPercentage = Math.round(percentage);
            const precisePercentage = percentage.toFixed(2);
            let chipClass = "chip-green";
            if (percentage >= 10 && percentage <= 50) chipClass = "chip-amber";
            else if (percentage > 50) chipClass = "chip-red";

            return (
              <Chip
                size="medium"
                title={`% of Public Assets: ${precisePercentage}%`}
                label={`${roundedPercentage}%`}
                className={`user-chip ${chipClass} assets`}
              />
            );
          },
        },
        {
          id: "activitiesAccumulated",
          title: "# of Activities",
          width: "23%",
          sorting: true,
          renderCell: (value) => {
            if (!value || Object.keys(value).length === 0) return "-";
            const totalValue = Object.values(value).reduce(
              (sum, v) => sum + v.count,
              0
            );
            return (
              <Chip
                size="medium"
                title={totalValue}
                label={formatNumber(totalValue)}
                color="primary"
                className="user-chip"
              />
            );
          },
        },
        {
          id: "locations",
          title: "Locations",
          align: "left",
          width: "19%",
          renderCell: (value) => {
            if (!value || value.length === 0 || Object.values(value) === 0)
              return "-";

            const validLocations = processCountryData(value);
            if (validLocations.length === 0) return "-";

            // Use the new generic function with internal state management
            return renderCountryNamesWithPopover(
              validLocations,
              3, // Show 3 countries before +remaining
              "location-flag-cell"
            );
          },
        },
        {
          id: "action",
          title: "Action",
          width: "15%",
          align: "center",
          renderCell: (value) => {
            const updatedData = {
              ...value,
              userName: isMasked
                ? maskUsername(value.userName)
                : startCase(camelCase(value.userName)),
            };
            return (
              <img
                title="Remove Public File"
                className="action_btn remove-public-file"
                alt="Remove Public File"
                src={remove_public_file}
                onClick={() => {
                  setRevokeDetails(updatedData);
                  setRevokePopup(true);
                }}
              />
            );
          },
        },
      ],
    },
  },

  behaviormodels: {
    id: "behaviormodels",
    title: "Behavior Models Details",
    apiEndpoint: "/dataguard/behaviorModel/data",
    FiltersListEndPoint: "/dataguard/behaviorModels/policySubtype/distinct",
    table: {
      pagination: true,
      columns: [
        {
          id: "policyName",
          title: "Policy",
          align: "left",
          width: 300,
          sorting: true,
          renderHeader: renderHeaderWithFilters(
            "Policy",
            "policy",
            policySubtypeFiltersList?.length,
            activeFilterField,
            handleFilterIconClick
          ),
          renderCell: (value) => {
            return (
              <div className="location-name-cell" title={value}>
                {value}
              </div>
            );
          },
        },
        {
          id: "users",
          title: "# of Users",
          width: 200,
          sorting: true,
          renderCell: (value) => {
            if (!value || Object.keys(value).length === 0) return "-";
            const totalValue = Object.values(value).reduce(
              (sum, v) => sum + (v.count || 0),
              0
            );
            return (
              <Chip
                size="medium"
                title="# of Users"
                label={totalValue}
                color="primary"
                className="user-chip"
              />
            );
          },
        },
        {
          id: "totalAnomaliesByDs",
          title: "# of Anomalies",
          align: "left",
          width: 300,
          sorting: true,
          renderCell: (value) => {
            if (!value || Object.keys(value).length === 0) return renderEmptyAnomaly();
            const valueArr = Object.values(value).map((v) => ({
              count: v.count,
              dataSourceName: v.dataSourceName,
              dataSourceType: v.dataSourceType,
            }));
            const totalCount = valueArr.reduce((sum, v) => sum + (v.count || 0), 0);
            if (totalCount === 0) return renderEmptyAnomaly();
            return renderDonutChart(valueArr);
          },
        },
        {
          id: "severity",
          title: "Severity",
          width: 200,
          renderHeader: renderHeaderWithFilters(
            "Severity",
            "severity",
            severityFiltersList?.length,
            activeFilterField,
            handleFilterIconClick
          ),
          renderCell: (value) => {
            if (!value) return "-";

            return (
              <SeverityChips
                high={value.high ?? 0}
                medium={value.medium ?? 0}
                low={value.low ?? 0}
              />
            );
          },
        },
        {
          id: "action",
          title: "Action",
          width: 200,
          renderCell: (value, row) => {
            return (
              <div className="action-cell">
                {value?.notification ? (
                  <NotificationsIcon
                    titleAccess="Turn Off Notification"
                    className="action_btn color-green"
                    onClick={() => handleNotificationClick(value, setNotificationDetails, setNotificationPopup)}
                  />
                ) : (
                  <NotificationsOffIcon
                    titleAccess="Turn On Notification"
                    className="action_btn color-red"
                    onClick={() => handleNotificationClick(value, setNotificationDetails, setNotificationPopup)}
                  />
                )}
                {value?.severity?.high > 0 || value?.severity?.medium > 0 || value?.severity?.low > 0 ? (
                 <img
                  onClick={() => handleAlertActionClick(value, setAlertActionDetails, setAlertActionPopup)}
                  title="Mark as Resolved/False Positive"
                  className="action_btn"
                  alt="Mark as Resolved/False Positive"
                  src={done_all}
                />) : (
                  <img
                    className="action_btn disabled"
                    alt="Mark as Resolved/False Positive"
                    src={done_all}
                    title="Action Unavailable"/>
                )}
              </div>
            );
          },
        },
      ],
    },
  },
});

export const getPageConfiguration = (
  pageId,
  setRevokeDetails,
  setRevokePopup,
  dispatch,
  isMasked,
  locationFiltersList,
  policySubtypeFiltersList,
  severityFiltersList,
  userRolesFiltersList,
  ownersFiltersList,
  handleFilterIconClick,
  activeFilterField,
  isIncognito,
  setNotificationDetails = () => { },
  setNotificationPopup = () => { },
  setAlertActionDetails = () => { },
  setAlertActionPopup = () => { }
) => {
  const configs = PAGE_CONFIGURATIONS(
    setRevokeDetails,
    setRevokePopup,
    dispatch,
    isMasked,
    locationFiltersList,
    policySubtypeFiltersList,
    severityFiltersList,
    userRolesFiltersList,
    ownersFiltersList,
    handleFilterIconClick,
    activeFilterField,
    isIncognito,
    setNotificationDetails,
    setNotificationPopup,
    setAlertActionDetails,
    setAlertActionPopup
  );
  return configs[pageId] || null;
};

